import { type FC, useMemo } from "react";
import { Alert, Dialog } from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useTagQuery, useUpdateTagMutation } from "@/partner/entities/tags";
import {
  mapTagToFormValues,
  mapFormValuesToUpdateTagInput,
  getTagTypeCustomFields,
  getTagMutationErrorMessage,
  type TagFormValues,
} from "../model";
import { TagForm } from "./TagForm";

interface EditTagDialogProps {
  open: boolean;
  tagId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditTagDialog: FC<EditTagDialogProps> = ({
  open,
  tagId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();

  const { data, loading } = useTagQuery({
    variables: { id: String(tagId) },
    skip: !tagId || !open,
  });

  const [updateTag, { loading: updating }] = useUpdateTagMutation({
    onCompleted: () => {
      enqueueSnackbar(t("tags.edit.success"), {
        variant: "success",
      });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(getTagMutationErrorMessage(error, t), {
        variant: "error",
      });
    },
    refetchQueries: ["TagsList"],
  });

  const initialValues = useMemo(() => mapTagToFormValues(data?.tag), [data]);

  const tag = data?.tag;
  // Страница удалена (platformPageId есть, а platformPage не пришла) или не
  // опубликована (нет publishAt): витрина нарисует тег стандартным шаблоном.
  const overrideBroken = Boolean(
    tag?.platformPageId && (!tag?.platformPage || !tag.platformPage.publishAt)
  );

  const warning = overrideBroken ? (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {t("tags.form.platformPageMissingWarning")}
    </Alert>
  ) : undefined;

  const handleSubmit = async (values: TagFormValues) => {
    // Определения полей типа читаются перед сохранением, и сетевой сбой
    // именно здесь иначе прошёл бы мимо обработчика ошибок мутации:
    // партнёр не увидел бы ни успеха, ни причины отказа.
    let customFields;
    try {
      customFields = await getTagTypeCustomFields(apolloClient, values.typeId);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : String(error), {
        variant: "error",
      });
      return;
    }

    // Диалог закрывается только через onCompleted мутации (см. выше):
    // при отказе (например TAG_CUSTOM_FIELD_INVALID) форма остаётся
    // открытой с введёнными значениями, а не закрывается вслепую.
    await updateTag({
      variables: {
        id: String(tagId),
        input: mapFormValuesToUpdateTagInput(values, customFields),
      },
    });
  };

  return (
    <Dialog
      fullScreen
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <TagForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={t("tags.edit.title")}
        loading={loading || updating}
        warning={warning}
      />
    </Dialog>
  );
};
